import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { pagesAPI, notificationsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Menu, X, Bell, User, LogOut, ChevronDown, BookOpen, FileText, Users, LayoutDashboard } from 'lucide-react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dynamicPages, setDynamicPages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    pagesAPI.list().then(res => {
      setDynamicPages(res.data.filter(p => p.visible_en_menu));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      notificationsAPI.list().then(res => {
        setUnreadCount(res.data.filter(n => !n.leida).length);
      }).catch(() => {});
    }
  }, [user, location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.roles?.includes('Admin') || user.roles?.includes('Editor')) return '/panel';
    if (user.roles?.includes('Revisor')) return '/panel/mis-revisiones';
    return '/panel/mis-manuscritos';
  };

  const navLinks = [
    { label: 'Inicio', to: '/' },
    { label: 'Archivo', to: '/archivo' },
    { label: 'Contacto', to: '/contacto' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-sm bg-slate-dark flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-slate-dark text-base leading-tight tracking-tight">Archivos Trans</span>
              <span className="text-[10px] font-sans text-gray-400 tracking-widest uppercase">ALET</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-3 py-2 text-sm font-sans font-medium rounded-md transition-colors duration-200 ${
                  isActive(link.to)
                    ? 'text-slate-dark bg-gray-100'
                    : 'text-gray-500 hover:text-slate-dark hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Dynamic CMS Pages */}
            {dynamicPages.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="px-3 py-2 text-sm font-sans font-medium text-gray-500 hover:text-slate-dark hover:bg-gray-50 rounded-md flex items-center gap-1" data-testid="nav-more-dropdown">
                    Secciones <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {dynamicPages.map(page => (
                    <DropdownMenuItem key={page.slug} onClick={() => navigate(`/pagina/${page.slug}`)} data-testid={`nav-page-${page.slug}`}>
                      {page.titulo}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Link to="/panel/notificaciones" className="relative p-2 text-gray-500 hover:text-slate-dark" data-testid="nav-notifications">
                  <Bell className="w-5 h-5" strokeWidth={1.5} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-trans-pink text-white text-[10px] font-bold flex items-center justify-center rounded-full">{unreadCount}</span>
                  )}
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-50 transition-colors" data-testid="nav-user-menu">
                      <div className="w-7 h-7 rounded-full bg-trans-blue/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-dark" strokeWidth={1.5} />
                      </div>
                      <span className="text-sm font-sans font-medium text-slate-dark">{user.name}</span>
                      <ChevronDown className="w-3 h-3 text-gray-400" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-slate-dark">{user.name} {user.apellidos}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                      <div className="flex gap-1 mt-1">
                        {user.roles?.map(r => (
                          <Badge key={r} variant="outline" className="text-[10px] px-1.5 py-0">{r}</Badge>
                        ))}
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="nav-dashboard-link">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Panel de Control
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login-btn" className="font-sans text-sm">
                  Iniciar Sesion
                </Button>
                <Button size="sm" onClick={() => navigate('/registro')} data-testid="nav-register-btn" className="font-sans text-sm bg-slate-dark hover:bg-slate-dark/90">
                  Registrarse
                </Button>
              </div>
            )}
          </div>

          {/* Mobile toggle */}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-gray-100 pt-3">
            <div className="flex flex-col gap-1">
              {navLinks.map(link => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2 text-sm font-sans rounded-md ${isActive(link.to) ? 'bg-gray-100 text-slate-dark font-medium' : 'text-gray-500'}`}>
                  {link.label}
                </Link>
              ))}
              {dynamicPages.map(page => (
                <Link key={page.slug} to={`/pagina/${page.slug}`} onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 text-sm font-sans text-gray-500 rounded-md">
                  {page.titulo}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to={getDashboardLink()} onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm font-sans text-gray-500">Panel de Control</Link>
                  <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="px-3 py-2 text-sm font-sans text-left text-red-500">Cerrar Sesion</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm font-sans text-gray-500">Iniciar Sesion</Link>
                  <Link to="/registro" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm font-sans text-trans-blue font-medium">Registrarse</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
